import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import esLocale from '@fullcalendar/core/locales/es';
import html2canvas from 'html2canvas';
import contentService from '../services/contentService';
import authService from '../services/authService';
import PostModal from './PostModal';
import PostDetailModal from './PostDetailModal';
import Loading from './Loading';
import VencidosModal from './VencidosModal';
import AprobacionesModal from './AprobacionesModal';
import PendientesEnvioModal from './PendientesEnvioModal';
import { tienePantalla } from '../utils/pantallas';
import './CalendarView.css';

// ── Modal de Carga Masiva ─────────────────────────────────────────────────────
const BulkImportModal = ({ onClose, onSuccess }) => {
  const [file, setFile]       = useState(null)
  const [loading, setLoading] = useState(false)
  const [result, setResult]   = useState(null)
  const [error, setError]     = useState(null)
  const fileRef = useRef(null)

  const handleFile = e => {
    const f = e.target.files[0]
    if (f) { setFile(f); setResult(null); setError(null) }
  }

  const handleSubmit = async () => {
    if (!file) return
    setLoading(true); setError(null); setResult(null)
    const fd = new FormData()
    fd.append('file', file)
    try {
      const { data } = await contentService.bulkImportPosts(fd)
      setResult(data)
      if (data.creados > 0) onSuccess()
    } catch (e) {
      setError(e.response?.data?.error || 'Error al procesar el archivo')
    } finally {
      setLoading(false)
    }
  }

  const downloadPlantilla = () => {
    // Generar CSV de ejemplo para descargar
    const csv = 'TITULO,DESARROLLA,PLATAFORMA,TIPO DE MATERIAL,CUENTA,FECHA PUBLICACION,FECHA VALIDACION,BRIEF\n' +
                'Post de ejemplo,Interno,Instagram,Reel,maicao_cl,2026-08-15,2026-08-12,Brief del post de ejemplo'
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a'); a.href = url; a.download = 'plantilla_posts.csv'; a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
    }}>
      <div style={{
        background: '#fff', borderRadius: 12, padding: 32, width: 520, maxWidth: '95vw',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1a1a1a' }}>
              📥 Carga Masiva de Posts
            </h2>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6b7280' }}>
              Sube un Excel con tus posts para crearlos automáticamente
            </p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#6b7280' }}>✕</button>
        </div>

        {/* Columnas esperadas */}
        <div style={{ background: '#f9fafb', borderRadius: 8, padding: '12px 16px', marginBottom: 20, fontSize: 12 }}>
          <p style={{ margin: '0 0 6px', fontWeight: 600, color: '#374151' }}>Columnas del Excel:</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {['TITULO','DESARROLLA','PLATAFORMA','TIPO DE MATERIAL','CUENTA','FECHA PUBLICACION','FECHA VALIDACION','BRIEF'].map(c => (
              <span key={c} style={{ background: '#e5e7eb', borderRadius: 4, padding: '2px 8px', fontFamily: 'monospace', color: '#374151' }}>{c}</span>
            ))}
          </div>
          <p style={{ margin: '8px 0 0', color: '#6b7280', fontSize: 11 }}>
            Formatos de fecha: YYYY-MM-DD o DD/MM/YYYY. Tipo de material: Reel, Historia, Carrusel, Imagen, Video.
          </p>
        </div>

        {/* Upload area */}
        <div
          onClick={() => fileRef.current?.click()}
          style={{
            border: '2px dashed #d1d5db', borderRadius: 8, padding: '24px 16px',
            textAlign: 'center', cursor: 'pointer', marginBottom: 16,
            background: file ? '#f0fdf4' : '#fafafa',
            borderColor: file ? '#16a34a' : '#d1d5db',
            transition: 'all 0.2s',
          }}
        >
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleFile} style={{ display: 'none' }} />
          <div style={{ fontSize: 32, marginBottom: 8 }}>{file ? '✅' : '📂'}</div>
          <p style={{ margin: 0, fontWeight: 600, color: file ? '#16a34a' : '#374151', fontSize: 14 }}>
            {file ? file.name : 'Haz click para seleccionar archivo'}
          </p>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: '#9ca3af' }}>
            {file ? `${(file.size / 1024).toFixed(1)} KB` : 'Soporta .xlsx, .xls'}
          </p>
        </div>

        {/* Resultado */}
        {result && (
          <div style={{
            background: result.errores?.length ? '#fffbeb' : '#f0fdf4',
            border: `1px solid ${result.errores?.length ? '#fcd34d' : '#86efac'}`,
            borderRadius: 8, padding: '12px 16px', marginBottom: 16
          }}>
            <p style={{ margin: '0 0 4px', fontWeight: 600, color: '#1a1a1a', fontSize: 14 }}>
              ✅ {result.creados} posts creados
              {result.errores?.length > 0 && ` · ⚠️ ${result.errores.length} errores`}
            </p>
            {result.errores?.length > 0 && (
              <ul style={{ margin: '8px 0 0', paddingLeft: 16, fontSize: 12, color: '#92400e' }}>
                {result.errores.slice(0, 5).map((e, i) => (
                  <li key={i}>Fila {e.fila}: {e.error}</li>
                ))}
                {result.errores.length > 5 && <li>...y {result.errores.length - 5} más</li>}
              </ul>
            )}
          </div>
        )}

        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#dc2626' }}>
            ❌ {error}
          </div>
        )}

        {/* Botones */}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between', alignItems: 'center' }}>
          <button onClick={downloadPlantilla} style={{
            background: 'none', border: '1px solid #d1d5db', borderRadius: 8,
            padding: '8px 14px', fontSize: 13, cursor: 'pointer', color: '#374151'
          }}>
            ⬇ Descargar plantilla
          </button>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={onClose} style={{
              background: '#f3f4f6', border: 'none', borderRadius: 8,
              padding: '10px 20px', fontSize: 14, cursor: 'pointer', color: '#374151'
            }}>
              Cancelar
            </button>
            <button
              onClick={handleSubmit}
              disabled={!file || loading}
              style={{
                background: file && !loading ? '#7c3aed' : '#e5e7eb',
                color: file && !loading ? '#fff' : '#9ca3af',
                border: 'none', borderRadius: 8, padding: '10px 24px',
                fontSize: 14, fontWeight: 600, cursor: file && !loading ? 'pointer' : 'not-allowed'
              }}
            >
              {loading ? 'Procesando...' : 'Importar Posts'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────

const CalendarView = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentUser = authService.getUser();
  const isCliente = currentUser?.role === 'Cliente';
  const calendarRef = useRef(null);
  const gridWrapperRef = useRef(null);
  const isAdminUser = currentUser?.username === 'admin';
  const [downloadingCalendar, setDownloadingCalendar] = useState(false);
  const [events, setEvents] = useState([]);
  const [allEvents, setAllEvents] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const isAdmin = currentUser?.is_superuser === true;
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);

  // Estados para filtros
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedInfluencers, setSelectedInfluencers] = useState([]);
  const [selectedMaterialTypes, setSelectedMaterialTypes] = useState([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [availableStatuses, setAvailableStatuses] = useState([]);
  const [availableInfluencers, setAvailableInfluencers] = useState([]);
  const [availableMaterialTypes, setAvailableMaterialTypes] = useState([]);
  const [availablePlatforms, setAvailablePlatforms] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showVencidos, setShowVencidos] = useState(false);
  const [markingPublished, setMarkingPublished] = useState(false);
  // Los avisos de entrada se muestran en secuencia (Vencidos → Aprobaciones → Pendientes de envío), nunca apilados.
  const [vencidosDone, setVencidosDone] = useState(false);
  const [aprobacionesDone, setAprobacionesDone] = useState(false);
  const [showViewDropdown, setShowViewDropdown] = useState(false);
  const getDefaultView = () => {
    if (window.innerWidth <= 480) return 'dayGridDay';
    if (window.innerWidth <= 768) return 'dayGridWeek';
    return 'dayGridWeek';
  };
  const [currentView, setCurrentView] = useState(getDefaultView);
  const [calendarTitle, setCalendarTitle] = useState('');

  // Obtener colores por estado
  const getEventColor = (status) => {
    const colors = {
      draft: { backgroundColor: '#9CA3AF', textColor: '#FFFFFF' },
      brief_pending: { backgroundColor: '#FCD34D', textColor: '#000000' },
      creative_development: { backgroundColor: '#60A5FA', textColor: '#FFFFFF' },
      creative_review: { backgroundColor: '#A78BFA', textColor: '#FFFFFF' },
      aprobacion_idea: { backgroundColor: '#8B5CF6', textColor: '#FFFFFF' },
      revision_copy_pendiente: { backgroundColor: '#EC4899', textColor: '#FFFFFF' },
      copy_review: { backgroundColor: '#F472B6', textColor: '#FFFFFF' },
      aprobacion_contenido: { backgroundColor: '#F97316', textColor: '#FFFFFF' },
      content_review: { backgroundColor: '#FB923C', textColor: '#FFFFFF' },
      final_review: { backgroundColor: '#FBBF24', textColor: '#000000' },
      approved: { backgroundColor: '#34D399', textColor: '#FFFFFF' },
      scheduled: { backgroundColor: '#10B981', textColor: '#FFFFFF' },
      published: { backgroundColor: '#059669', textColor: '#FFFFFF' },
      rejected: { backgroundColor: '#EF4444', textColor: '#FFFFFF' },
      no_publicado: { backgroundColor: '#6B7280', textColor: '#FFFFFF' }
    };

    return colors[status] || { backgroundColor: '#6B7280', textColor: '#FFFFFF' };
  };

  // 🆕 Función para actualizar un único post sin recargar todos (DEFINIDA ANTES DE USEEFFECT)
  const updateSinglePost = useCallback(async (postId) => {
    try {
      const response = await contentService.getPost(postId);
      const updatedPost = response.data;

      // Crear evento actualizado
      const eventColor = getEventColor(updatedPost.status);

      // Combinar fecha y hora de publicación si existe
      let eventStart = updatedPost.publication_date;
      let isAllDay = true;

      if (updatedPost.publication_time) {
        // Si hay hora de publicación, crear datetime completo
        eventStart = `${updatedPost.publication_date}T${updatedPost.publication_time}`;
        isAllDay = false;
      }

      const updatedEvent = {
        id: updatedPost.id,
        title: updatedPost.title,
        start: eventStart,
        allDay: isAllDay,
        backgroundColor: eventColor.backgroundColor,
        borderColor: updatedPost.desarrolla_color || eventColor.backgroundColor,
        textColor: eventColor.textColor,
        extendedProps: {
          post: updatedPost,
          platformIcon: updatedPost.platform_icon_url,
          influencerImage: updatedPost.influencer_profile_image_url,
          influencerName: updatedPost.influencer_name,
          platformName: updatedPost.platform_name,
          status: updatedPost.status,
          post_patrocinado: updatedPost.post_patrocinado
        }
      };

      // Actualizar en allEvents
      setAllEvents(prevAllEvents => {
        // Si el post está eliminado, removerlo del calendario
        if (updatedPost.status === 'eliminado') {
          const updatedAllEvents = prevAllEvents.filter(event => event.id !== postId);

          // Reaplicar filtros
          const filtered = updatedAllEvents.filter(event => {
            const eventStatus = event.extendedProps.status;
            const eventInfluencer = event.extendedProps.influencerName;

            const statusMatch = selectedStatuses.length === 0 || selectedStatuses.includes(eventStatus);
            const influencerMatch = selectedInfluencers.length === 0 || selectedInfluencers.includes(eventInfluencer);

            return statusMatch && influencerMatch;
          });

          setEvents(filtered);
          return updatedAllEvents;
        }

        // Si el post no está eliminado, actualizar o agregar el evento
        const existingEventIndex = prevAllEvents.findIndex(event => event.id === postId);
        let updatedAllEvents;

        if (existingEventIndex >= 0) {
          // El evento ya existe, actualizarlo
          updatedAllEvents = prevAllEvents.map(event =>
            event.id === postId ? updatedEvent : event
          );
        } else {
          // El evento no existe (nuevo post), agregarlo
          updatedAllEvents = [...prevAllEvents, updatedEvent];
        }

        // Reaplicar filtros
        const filtered = updatedAllEvents.filter(event => {
          const eventStatus = event.extendedProps.status;
          const eventInfluencer = event.extendedProps.influencerName;

          const statusMatch = selectedStatuses.length === 0 || selectedStatuses.includes(eventStatus);
          const influencerMatch = selectedInfluencers.length === 0 || selectedInfluencers.includes(eventInfluencer);

          return statusMatch && influencerMatch;
        });

        setEvents(filtered);
        return updatedAllEvents;
      });

    } catch (error) {
      console.error('Error al actualizar post específico:', error);
    }
  }, [selectedStatuses, selectedInfluencers]);

  useEffect(() => {
    loadPosts();
  }, []);

  // Abrir modal de post si viene desde link de email o notificación push (?open_post=123)
  const tryOpenPostFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    const openPostId = params.get('open_post');
    if (openPostId) {
      const postId = parseInt(openPostId);
      if (!isNaN(postId)) {
        setSelectedPost({ id: postId });
        setShowDetailModal(true);
        // Limpiar param sin recargar
        window.history.replaceState({}, '', window.location.pathname);
      }
    }
  };

  // Al montar y cada vez que cambian los searchParams (navegación normal / email)
  useEffect(() => {
    tryOpenPostFromUrl();
  }, [searchParams]);

  // Cuando la PWA vuelve al frente tras recibir una notificación push (iOS)
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') tryOpenPostFromUrl();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  // Actualizar título del calendario al cargar
  useEffect(() => {
    const updateTitle = () => {
      const calendarApi = calendarRef.current?.getApi();
      if (calendarApi && calendarApi.view) {
        setCalendarTitle(calendarApi.view.title);
        return true;
      }
      return false;
    };

    // Intentar actualizar inmediatamente
    if (!updateTitle()) {
      // Si falla, intentar después de 200ms
      const timer1 = setTimeout(() => {
        if (!updateTitle()) {
          // Si aún falla, intentar después de 500ms
          const timer2 = setTimeout(updateTitle, 500);
          return () => clearTimeout(timer2);
        }
      }, 200);
      return () => clearTimeout(timer1);
    }
  }, []);

  // Cerrar dropdown de vista al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showViewDropdown && !event.target.closest('.view-dropdown-container')) {
        setShowViewDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showViewDropdown]);

  // 🆕 Escuchar cambios cuando se cierra ViewPostPage (para acceso directo vía URL)
  useEffect(() => {
    const handlePostUpdated = (event) => {
      const postId = event.detail?.postId;
      if (postId) {
        updateSinglePost(postId);
      }
    };

    const handleStorageChange = (event) => {
      if (event.key === 'postUpdatedId' && event.newValue) {
        updateSinglePost(parseInt(event.newValue));
        // Limpiar localStorage
        localStorage.removeItem('postUpdatedId');
      }
    };

    // Escuchar evento personalizado (cambios en la misma tab)
    window.addEventListener('postUpdated', handlePostUpdated);

    // Escuchar evento de storage (cambios en otras tabs)
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('postUpdated', handlePostUpdated);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [updateSinglePost]);

  const markOverdueAsPublished = async () => {
    if (!window.confirm(`¿Marcar los ${vencidosCount} posts vencidos como publicados?`)) return;
    setMarkingPublished(true);
    try {
      const res = await contentService.markOverdueAsPublished();
      const updated = res.data?.updated ?? 0;
      alert(`✓ ${updated} posts marcados como publicados.`);
      loadPosts(true);
    } catch (e) {
      alert('Error al actualizar posts.');
    } finally {
      setMarkingPublished(false);
    }
  };

  const loadPosts = async (skipLoading = false, customStartDate = null, customEndDate = null) => {
    if (!skipLoading) {
      setLoading(true);
    }
    setLoadError(false);
    try {
      // Usar fechas personalizadas o calcular rango del mes actual
      let startDate, endDate;

      if (customStartDate && customEndDate) {
        startDate = customStartDate;
        endDate = customEndDate;
      } else {
        // Rango inicial: solo mes actual (optimizado)
        const now = new Date();
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        startDate = start.toISOString().split('T')[0];
        endDate = end.toISOString().split('T')[0];
      }

      const response = await contentService.getCalendar(startDate, endDate);

      // Filtrar posts eliminados
      const postsActivos = response.data.posts.filter(post => post.status !== 'eliminado');

      const formattedEvents = postsActivos.map(post => {
        const eventColor = getEventColor(post.status);

        // Combinar fecha y hora de publicación si existe
        let eventStart = post.publication_date;
        let isAllDay = true;

        if (post.publication_time) {
          // Si hay hora de publicación, crear datetime completo
          eventStart = `${post.publication_date}T${post.publication_time}`;
          isAllDay = false;
        }

        return {
          id: post.id,
          title: post.title,
          start: eventStart,
          allDay: isAllDay,
          backgroundColor: eventColor.backgroundColor,
          borderColor: post.desarrolla_color || eventColor.backgroundColor,
          textColor: eventColor.textColor,
          extendedProps: {
            post: post,
            platformIcon: post.platform_icon_url,
            influencerImage: post.influencer_profile_image_url,
            influencerName: post.influencer_name,
            platformName: post.platform_name,
            status: post.status,
            has_pending_media: post.has_pending_media,
            has_pending_creative_idea: post.has_pending_creative_idea,
            has_pending_copy: post.has_pending_copy,
            post_patrocinado: post.post_patrocinado,
            material_type: post.material_type
          }
        };
      });

      setAllEvents(formattedEvents);
      setEvents(formattedEvents);

      // Extraer influencers únicos
      const influencersMap = new Map();
      postsActivos.forEach(post => {
        if (post.influencer && post.influencer_name) {
          influencersMap.set(post.influencer, {
            id: post.influencer,
            name: post.influencer_name,
            profile_image_url: post.influencer_profile_image_url
          });
        }
      });
      setAvailableInfluencers(Array.from(influencersMap.values()));

      // Extraer tipos de contenido únicos
      const materialTypesUnicos = [...new Set(
        postsActivos.map(post => post.material_type).filter(Boolean)
      )];
      setAvailableMaterialTypes(materialTypesUnicos);

      // Extraer plataformas únicas (con su ícono) para el filtro
      const platformsMap = new Map();
      postsActivos.forEach(post => {
        if (post.platform_name && !platformsMap.has(post.platform_name)) {
          platformsMap.set(post.platform_name, {
            name: post.platform_name,
            icon: post.platform_icon_url
          });
        }
      });
      setAvailablePlatforms(Array.from(platformsMap.values()));

      // Extraer estados únicos
      const statusesUnicos = [...new Set(
        postsActivos.map(post => post.status).filter(status => status)
      )];

      // Agregar estado virtual 'aprobacion_contenido' si hay posts con archivos pendientes
      if (postsActivos.some(post => post.has_pending_media)) {
        statusesUnicos.push('aprobacion_contenido');
      }

      // Agregar estado virtual 'aprobacion_idea' si hay posts con idea creativa pendiente
      if (postsActivos.some(post => post.has_pending_creative_idea)) {
        statusesUnicos.push('aprobacion_idea');
      }

      // Agregar estado virtual 'revision_copy' si hay posts con copy pendiente de revisión
      if (postsActivos.some(post => post.has_pending_copy)) {
        statusesUnicos.push('revision_copy_pendiente');
      }

      setAvailableStatuses(statusesUnicos);

    } catch (error) {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  // Calcular posts vencidos: fecha pasada y no publicados
  const today = new Date(); today.setHours(0,0,0,0);
  const vencidosCount = allEvents.filter(ev => {
    const st = ev.extendedProps?.status;
    if (st === 'published' || st === 'eliminado' || st === 'no_publicado') return false;
    const d = ev.extendedProps?.post?.publication_date || ev.startStr;
    if (!d) return false;
    return new Date(d.slice(0,10)) < today;
  }).length;

  // Aplicar filtros
  useEffect(() => {
    let filtered = [...allEvents];

    if (selectedStatuses.length > 0) {
      filtered = filtered.filter(event => {
        // Filtro especial: posts con archivos de contenido pendientes de aprobación
        if (selectedStatuses.includes('aprobacion_contenido') && event.extendedProps.has_pending_media) {
          return true;
        }
        // Filtro especial: posts con idea creativa pendiente de aprobación
        if (selectedStatuses.includes('aprobacion_idea') && event.extendedProps.has_pending_creative_idea) {
          return true;
        }
        // Filtro especial: posts con copy pendiente de aprobación
        if (selectedStatuses.includes('revision_copy_pendiente') && event.extendedProps.has_pending_copy) {
          return true;
        }
        // Filtro estándar por estado del post
        const virtualStatuses = ['aprobacion_contenido', 'aprobacion_idea', 'revision_copy_pendiente'];
        const otrosEstados = selectedStatuses.filter(s => !virtualStatuses.includes(s));
        return otrosEstados.includes(event.extendedProps.status);
      });
    }

    if (selectedInfluencers.length > 0) {
      filtered = filtered.filter(event =>
        event.extendedProps.post.influencer &&
        selectedInfluencers.includes(event.extendedProps.post.influencer)
      );
    }

    if (selectedMaterialTypes.length > 0) {
      filtered = filtered.filter(event =>
        selectedMaterialTypes.includes(event.extendedProps.material_type)
      );
    }

    if (selectedPlatforms.length > 0) {
      filtered = filtered.filter(event =>
        selectedPlatforms.includes(event.extendedProps.platformName)
      );
    }

    if (searchTerm.trim() !== '') {
      filtered = filtered.filter(event =>
        event.title.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (showVencidos) {
      const now = new Date(); now.setHours(0,0,0,0);
      filtered = filtered.filter(ev => {
        const st = ev.extendedProps?.status;
        if (st === 'published' || st === 'eliminado' || st === 'no_publicado') return false;
        const d = ev.extendedProps?.post?.publication_date || ev.startStr;
        if (!d) return false;
        return new Date(d.slice(0,10)) < now;
      });
    }

    setEvents(filtered);
  }, [selectedStatuses, selectedInfluencers, selectedMaterialTypes, selectedPlatforms, searchTerm, allEvents, showVencidos]);

  const handleDateClick = (info) => {
    const publicationDate = new Date(info.date).toISOString().slice(0, 16);
    setSelectedPost({ publication_date: publicationDate });
    setShowCreateModal(true);
  };

  const handleEventClick = (info) => {
    const post = info.event.extendedProps.post;
    setSelectedPost(post);
    setShowDetailModal(true);
  };

  const handleModalClose = () => {
    setShowCreateModal(false);
    setShowDetailModal(false);
    setSelectedPost(null);
  };

  const handleUpdate = (postId) => {
    // Usar el postId pasado como parámetro o el ID del post seleccionado
    const idToUpdate = postId || (selectedPost && selectedPost.id);
    if (idToUpdate) {
      updateSinglePost(idToUpdate);
    }
    // Además del patch puntual, se refresca el rango visible completo. Esto asegura
    // que la card quede al día aunque el patch puntual (updateSinglePost) no traiga
    // exactamente los mismos campos que la carga inicial (loadPosts), o si el
    // patch falla silenciosamente por algún cambio de forma en la respuesta del API.
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi && calendarApi.view) {
      const startDate = calendarApi.view.activeStart.toISOString().split('T')[0];
      const endDate = calendarApi.view.activeEnd.toISOString().split('T')[0];
      loadPosts(true, startDate, endDate);
    } else {
      loadPosts(true);
    }
  };

  const handleDatesSet = (dateInfo) => {
    // Handler para cuando cambia la vista del calendario
    // Actualizar el título del calendario
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      setCalendarTitle(calendarApi.view.title);
    }

    // Cargar datos del nuevo rango de fechas (carga bajo demanda)
    const startDate = dateInfo.startStr.split('T')[0];
    const endDate = dateInfo.endStr.split('T')[0];
    loadPosts(true, startDate, endDate);
  };

  const handleViewChange = (viewName) => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.changeView(viewName);
      setCurrentView(viewName);
      setShowViewDropdown(false);
    }
  };

  const getViewLabel = (viewName) => {
    const labels = {
      'dayGridMonth': 'Mes',
      'dayGridWeek': 'Semana',
      'dayGridDay': 'Día',
      'listMonth': 'Lista'
    };
    return labels[viewName] || 'Semana';
  };

  const handlePrev = () => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.prev();
    }
  };

  const handleNext = () => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.next();
    }
  };

  const handleToday = () => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.today();
    }
  };

  const renderEventContent = (eventInfo) => {
    const { platformIcon, influencerImage, status, post, post_patrocinado } = eventInfo.event.extendedProps;

    // Icono de tipo de material — mismos íconos que los botones del
    // filtro "Tipo de Contenido" (bi-image, bi-camera-reels, bi-collection,
    // bi-clock-history, bi-film), para que sea consistente en toda la app.
    // Antes solo cubría reel/image/carousel de Instagram y "Historia"
    // (story) no mostraba ningún icono.
    const MATERIAL_TYPE_ICONS = {
      image: 'bi-image',
      video: 'bi-camera-reels',
      carousel: 'bi-collection',
      story: 'bi-clock-history',
      reel: 'bi-film',
    };

    const getMaterialTypeIcon = () => {
      if (!post) return null;

      // Se muestra para cualquier red (Instagram, TikTok, Facebook), según
      // el Tipo de Material seleccionado en el post.
      const materialType = post.material_type?.toLowerCase();
      return MATERIAL_TYPE_ICONS[materialType] || null;
    };

    const materialTypeIcon = getMaterialTypeIcon();

    return (
      <div className="fc-event-content-wrapper" style={{ position: 'relative' }}>
        <div className="fc-event-header">
          <div className="fc-event-icons">
            {platformIcon && (
              <img
                src={platformIcon}
                alt="platform"
                className="fc-event-platform-icon"
                onError={(e) => e.target.style.display = 'none'}
              />
            )}
            {influencerImage && (
              <img
                src={influencerImage}
                alt="influencer"
                className="fc-event-influencer-icon"
                onError={(e) => e.target.style.display = 'none'}
              />
            )}
          </div>
        </div>
        {materialTypeIcon && (
          <div style={{
            position: 'absolute',
            top: '4px',
            right: '4px',
            zIndex: 10
          }}>
            <i
              className={`bi ${materialTypeIcon}`}
              style={{ fontSize: '10px', opacity: 0.7, color: '#374151' }}
            />
          </div>
        )}
        <div className="fc-event-body">
          <div className="fc-event-title">{eventInfo.event.title}</div>
          {post?.etiqueta && (
            <div style={{
              display: 'inline-block',
              alignSelf: 'flex-start',
              padding: '1px 7px',
              borderRadius: '9999px',
              fontSize: '0.62rem',
              fontWeight: '600',
              backgroundColor: post.etiqueta_color || '#3B82F6',
              color: '#fff',
              marginTop: '2px',
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {post.etiqueta}
            </div>
          )}
          {post_patrocinado && (
            <div style={{ marginTop: '3px' }}>
              <span title="Post Patrocinado" style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: '14px', height: '14px',
                background: '#f59e0b', borderRadius: '50%',
              }}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 12"
                  width="8" height="8" fill="white">
                  <text x="50%" y="9" textAnchor="middle" fontSize="9" fontWeight="bold"
                    fontFamily="Arial, sans-serif">$</text>
                </svg>
              </span>
            </div>
          )}
          {(status === 'published' || status === 'Publicado') && post && (
            <div className="fc-event-metrics">
              {/* Círculo verde indicador de publicado */}
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: '#10b981',
                flexShrink: 0,
                marginRight: '4px'
              }} title="Publicado" />

              {post.instagram_likes !== null && post.instagram_likes !== undefined && (
                <div className="fc-metric-item">
                  <img
                    src="https://gevesmaicaobucket.fra1.cdn.digitaloceanspaces.com/media/instagram-iconos/icono-like.png"
                    alt="likes"
                    className="fc-metric-icon"
                  />
                  <span className="fc-metric-value">{post.instagram_likes.toLocaleString()}</span>
                </div>
              )}
              {post.instagram_comments !== null && post.instagram_comments !== undefined && (
                <div className="fc-metric-item">
                  <img
                    src="https://gevesmaicaobucket.fra1.cdn.digitaloceanspaces.com/media/instagram-iconos/icono-comentario.png"
                    alt="comentarios"
                    className="fc-metric-icon"
                  />
                  <span className="fc-metric-value">{post.instagram_comments.toLocaleString()}</span>
                </div>
              )}
              {post.instagram_shares !== null && post.instagram_shares !== undefined && (
                <div className="fc-metric-item">
                  <img
                    src="https://gevesmaicaobucket.fra1.cdn.digitaloceanspaces.com/media/instagram-iconos/icono-compartir.png"
                    alt="compartidos"
                    className="fc-metric-icon"
                  />
                  <span className="fc-metric-value">{post.instagram_shares.toLocaleString()}</span>
                </div>
              )}
              {post.instagram_reposts !== null && post.instagram_reposts !== undefined && (
                <div className="fc-metric-item">
                  <img
                    src="https://gevesmaicaobucket.fra1.cdn.digitaloceanspaces.com/media/instagram-iconos/icono-reposts.png"
                    alt="reposts"
                    className="fc-metric-icon"
                  />
                  <span className="fc-metric-value">{post.instagram_reposts.toLocaleString()}</span>
                </div>
              )}
              {post.instagram_saved !== null && post.instagram_saved !== undefined && (
                <div className="fc-metric-item">
                  <img
                    src="https://gevesmaicaobucket.fra1.cdn.digitaloceanspaces.com/media/instagram-iconos/icono-guardar.png"
                    alt="guardados"
                    className="fc-metric-icon"
                  />
                  <span className="fc-metric-value">{post.instagram_saved.toLocaleString()}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Aplicar el color del borde del desarrolla después de montar el evento
  const handleEventDidMount = (info) => {
    const borderColor = info.event.borderColor;
    if (borderColor && info.el) {
      info.el.style.borderLeftColor = borderColor;
    }
  };

  // Filtros handlers
  const handleStatusFilterClick = (status) => {
    setSelectedStatuses(prev => {
      if (prev.includes(status)) {
        return prev.filter(s => s !== status);
      } else {
        return [...prev, status];
      }
    });
  };

  const handleInfluencerFilterClick = (influencerId) => {
    setSelectedInfluencers(prev => {
      if (prev.includes(influencerId)) {
        return prev.filter(id => id !== influencerId);
      } else {
        return [...prev, influencerId];
      }
    });
  };

  const handleMaterialTypeFilterClick = (materialType) => {
    setSelectedMaterialTypes(prev => {
      if (prev.includes(materialType)) {
        return prev.filter(mt => mt !== materialType);
      } else {
        return [...prev, materialType];
      }
    });
  };

  const handlePlatformFilterClick = (platformName) => {
    setSelectedPlatforms(prev =>
      prev.includes(platformName)
        ? prev.filter(n => n !== platformName)
        : [...prev, platformName]
    );
  };

  const clearFilters = () => {
    setSelectedPlatforms([]);
    setSelectedStatuses([]);
    setSelectedInfluencers([]);
    setSelectedMaterialTypes([]);
    setSearchTerm('');
  };

  const handleDownloadCalendar = async () => {
    if (!gridWrapperRef.current || downloadingCalendar) return;
    setDownloadingCalendar(true);

    // El wrapper tiene max-height + overflow-y: auto para scrollear en pantalla.
    // Para la captura necesitamos que se expanda a su alto real (todas las semanas),
    // si no html2canvas solo captura lo que está visible dentro del scroll.
    const el = gridWrapperRef.current;
    const prevStyle = {
      maxHeight: el.style.maxHeight,
      overflowY: el.style.overflowY,
      overflowX: el.style.overflowX,
      height: el.style.height,
    };
    el.style.maxHeight = 'none';
    el.style.overflowY = 'visible';
    el.style.overflowX = 'visible';
    el.style.height = 'auto';
    // Dar un frame para que FullCalendar re-mida el layout con el nuevo alto.
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

    try {
      const canvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
        height: el.scrollHeight,
        windowHeight: el.scrollHeight,
      });
      const dataUrl = canvas.toDataURL('image/png');
      const a = document.createElement('a');
      const tituloSlug = (calendarTitle || 'calendario')
        .toLowerCase()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
      a.href = dataUrl;
      a.download = `calendario_${tituloSlug || 'vista'}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (error) {
      console.error('Error al descargar el calendario:', error);
      alert('No se pudo generar la imagen del calendario. Intenta nuevamente.');
    } finally {
      el.style.maxHeight = prevStyle.maxHeight;
      el.style.overflowY = prevStyle.overflowY;
      el.style.overflowX = prevStyle.overflowX;
      el.style.height = prevStyle.height;
      setDownloadingCalendar(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  };

  return (
    <div className="calendar-container">
      {/* Header */}
      <div className="calendar-header">
        <div>
          <h1 className="calendar-title">Planificación</h1>
        </div>

      </div>

      {/* Barra de controles — 2 filas semánticas */}
      <div className="calendar-controls-bar">

        {/* Fila 1: navegación + mes/año + botón Nuevo */}
        <div className="cal-bar-row cal-bar-row--nav">
          <div className="calendar-nav-group">
            <button onClick={handlePrev} className="cal-nav-btn cal-nav-btn--icon" title="Anterior">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <button onClick={handleToday} className="cal-nav-btn cal-nav-btn--today">Hoy</button>
            <button onClick={handleNext} className="cal-nav-btn cal-nav-btn--icon" title="Siguiente">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>

          <div className="calendar-title-display">
            {calendarTitle || 'Cargando...'}
          </div>

          <div className="calendar-separator"></div>

          {!isCliente && (
            <>
              {isAdmin && false && (
                <button
                  onClick={() => setShowBulkImport(true)}
                  className="cal-btn-nuevo"
                  style={{ background: '#7c3aed', marginRight: 6 }}
                  title="Carga masiva desde Excel"
                >
                  📥 Importar
                </button>
              )}
              <button
                onClick={() => { setSelectedPost(null); setShowCreateModal(true); }}
                className="cal-btn-nuevo"
              >
                + Nuevo
              </button>
            </>
          )}
        </div>

        {/* Fila 2: filtros + vista + búsqueda */}
        <div className="cal-bar-row cal-bar-row--filters">

          {/* Filtros */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`estado-btn estado-btn--icon-only ${showFilters ? 'active' : ''}`}
            title={showFilters ? 'Ocultar filtros' : 'Filtros'}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
            </svg>
            <span className="calendar-btn-label">{showFilters ? 'Ocultar' : 'Filtros'}</span>
            {(selectedStatuses.length > 0 || selectedInfluencers.length > 0 || selectedMaterialTypes.length > 0 || selectedPlatforms.length > 0) && (
              <span className={`cal-badge ${showFilters ? 'cal-badge--inverted' : ''}`}>
                {selectedStatuses.length + selectedInfluencers.length + selectedMaterialTypes.length + selectedPlatforms.length}
              </span>
            )}
          </button>

          {/* Vencidos: se quitó el botón de este toolbar — ya existe la
              pantalla dedicada "Vencidos" en el menú lateral para eso. */}

          {/* Vista */}
          <div className="view-dropdown-container">
            <button
              onClick={() => setShowViewDropdown(!showViewDropdown)}
              className={`estado-btn estado-btn--icon-only ${showViewDropdown ? 'active' : ''}`}
              title={`Vista: ${getViewLabel(currentView)}`}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              <span className="calendar-btn-label">Vista: {getViewLabel(currentView)}</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: showViewDropdown ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>

            {showViewDropdown && (
              <div className="view-dropdown">
                {[
                  { view: 'dayGridMonth', label: 'Mes' },
                  { view: 'dayGridWeek', label: 'Semana' },
                  { view: 'dayGridDay', label: 'Día' },
                  { view: 'listMonth', label: 'Lista' }
                ].map(({ view, label }) => (
                  <button
                    key={view}
                    onClick={() => handleViewChange(view)}
                    className={`view-option ${currentView === view ? 'view-option--active' : ''}`}
                  >
                    <span>{label}</span>
                    {currentView === view && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Descargar calendario (solo usuario admin) */}
          {isAdminUser && (
            <button
              onClick={handleDownloadCalendar}
              disabled={downloadingCalendar}
              className="estado-btn estado-btn--icon-only"
              title="Descargar calendario (vista y filtros actuales)"
            >
              {downloadingCalendar ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'spin 0.8s linear infinite' }}>
                  <line x1="12" y1="2" x2="12" y2="6" />
                  <line x1="12" y1="18" x2="12" y2="22" />
                  <line x1="4.93" y1="4.93" x2="7.76" y2="7.76" />
                  <line x1="16.24" y1="16.24" x2="19.07" y2="19.07" />
                  <line x1="2" y1="12" x2="6" y2="12" />
                  <line x1="18" y1="12" x2="22" y2="12" />
                  <line x1="4.93" y1="19.07" x2="7.76" y2="16.24" />
                  <line x1="16.24" y1="7.76" x2="19.07" y2="4.93" />
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
              )}
              <span className="calendar-btn-label">{downloadingCalendar ? 'Generando...' : 'Descargar'}</span>
            </button>
          )}

          {/* Búsqueda */}
          <div className="calendar-search-wrapper">
            <input
              type="text"
              className="calendar-search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar..."
            />
            {searchTerm && (
              <button className="calendar-search-clear" onClick={() => setSearchTerm('')}>×</button>
            )}
          </div>

        </div>
      </div>

      {/* Filters Section */}
      {showFilters && (
        <div style={{ marginBottom: '1rem' }}>
          {/* Status Filters — mismo diseño de chip que Tipo de Contenido/Influencer (caja gris + círculo + check), tamaño reducido */}
          <div style={{
            padding: '0.4rem',
            backgroundColor: '#f9fafb',
            borderRadius: '0.5rem',
            border: '1px solid #e5e7eb',
            marginBottom: '1rem'
          }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            {[
              { status: 'draft', label: 'Borrador' },
              { status: 'brief_pending', label: 'Brief Pendiente' },
              { status: 'creative_development', label: 'Desarrollo Creativo' },
              { status: 'aprobacion_idea', label: 'Aprobación Idea' },
              { status: 'revision_copy_pendiente', label: 'Aprobar Copy' },
              { status: 'aprobacion_contenido', label: 'Aprobación Contenido' },
              { status: 'content_review', label: 'Revisión de Contenido' },
              { status: 'final_review', label: 'Revisión Final' },
              { status: 'approved', label: 'Aprobado' },
              { status: 'scheduled', label: 'Programado' },
              { status: 'published', label: 'Publicado' },
              { status: 'rejected', label: 'Rechazado' },
              { status: 'no_publicado', label: 'No Publicado' },
            ]
            .filter(({ status }) => availableStatuses.includes(status))
            .map(({ status, label }) => {
              const isSelected = selectedStatuses.includes(status);
              const eventStyle = getEventColor(status);

              return (
                <button
                  key={status}
                  onClick={() => handleStatusFilterClick(status)}
                  title={label}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                    padding: '0.3rem 0.55rem',
                    border: isSelected ? '3px solid #3b82f6' : '2px solid #e5e7eb',
                    borderRadius: '2rem',
                    backgroundColor: 'white',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    transform: isSelected ? 'scale(1.05)' : 'scale(1)',
                    boxShadow: isSelected ? '0 4px 6px -1px rgba(0, 0, 0, 0.1)' : 'none'
                  }}
                  onMouseOver={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.transform = 'scale(1.05)';
                      e.currentTarget.style.boxShadow = '0 2px 4px -1px rgba(0, 0, 0, 0.1)';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.transform = 'scale(1)';
                      e.currentTarget.style.boxShadow = 'none';
                    }
                  }}
                >
                  <div style={{ position: 'relative' }}>
                    <span
                      style={{
                        display: 'block',
                        width: '22px',
                        height: '22px',
                        borderRadius: '50%',
                        backgroundColor: eventStyle.backgroundColor,
                        flexShrink: 0
                      }}
                    />
                    {isSelected && (
                      <div style={{
                        position: 'absolute',
                        bottom: '-2px',
                        right: '-2px',
                        width: '13px',
                        height: '13px',
                        borderRadius: '50%',
                        backgroundColor: '#3b82f6',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '2px solid white',
                        fontSize: '0.55rem',
                        color: 'white',
                        fontWeight: 'bold'
                      }}>
                        ✓
                      </div>
                    )}
                  </div>
                  <span style={{
                    fontSize: '0.78rem',
                    fontWeight: isSelected ? '600' : '500',
                    color: isSelected ? '#1f2937' : '#6b7280'
                  }}>
                    {label}
                  </span>
                </button>
              );
            })}
            {(selectedStatuses.length > 0 || selectedInfluencers.length > 0 || selectedMaterialTypes.length > 0 || selectedPlatforms.length > 0 || searchTerm.trim() !== '') && (
              <button
                onClick={clearFilters}
                style={{
                  padding: '0.3rem 0.6rem',
                  border: '1px solid #ef4444',
                  borderRadius: '2rem',
                  backgroundColor: 'white',
                  color: '#ef4444',
                  cursor: 'pointer',
                  fontSize: '0.78rem',
                  fontWeight: '600',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#ef4444';
                  e.currentTarget.style.color = 'white';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = 'white';
                  e.currentTarget.style.color = '#ef4444';
                }}
              >
                ✕ Limpiar filtros
              </button>
            )}
          </div>
        </div>

        {/* Tipo de Contenido Filters — mismo diseño que Influencer (caja gris + chips redondeados) */}
        {availableMaterialTypes.length > 0 && (
          <div style={{
            padding: '0.4rem',
            backgroundColor: '#f9fafb',
            borderRadius: '0.5rem',
            border: '1px solid #e5e7eb',
            marginBottom: '1rem'
          }}>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
              {[
                { type: 'image', label: 'Imagen', icon: 'bi-image' },
                { type: 'video', label: 'Video', icon: 'bi-camera-reels' },
                { type: 'carousel', label: 'Carrusel', icon: 'bi-collection' },
                { type: 'story', label: 'Historia', icon: 'bi-clock-history' },
                { type: 'reel', label: 'Reel', icon: 'bi-film' },
              ]
              .filter(({ type }) => availableMaterialTypes.includes(type))
              .map(({ type, label, icon }) => {
                const isSelected = selectedMaterialTypes.includes(type);
                return (
                  <button
                    key={type}
                    onClick={() => handleMaterialTypeFilterClick(type)}
                    title={label}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.4rem',
                      padding: '0.3rem 0.55rem',
                      border: isSelected ? '3px solid #3b82f6' : '2px solid #e5e7eb',
                      borderRadius: '2rem',
                      backgroundColor: 'white',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      transform: isSelected ? 'scale(1.05)' : 'scale(1)',
                      boxShadow: isSelected ? '0 4px 6px -1px rgba(0, 0, 0, 0.1)' : 'none'
                    }}
                    onMouseOver={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.transform = 'scale(1.05)';
                        e.currentTarget.style.boxShadow = '0 2px 4px -1px rgba(0, 0, 0, 0.1)';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.boxShadow = 'none';
                      }
                    }}
                  >
                    <div style={{ position: 'relative' }}>
                      <div style={{
                        display: 'flex',
                        width: '22px',
                        height: '22px',
                        borderRadius: '50%',
                        backgroundColor: '#e5e7eb',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.75rem',
                        color: '#6b7280'
                      }}>
                        <i className={`bi ${icon}`} />
                      </div>
                      {isSelected && (
                        <div style={{
                          position: 'absolute',
                          bottom: '-2px',
                          right: '-2px',
                          width: '13px',
                          height: '13px',
                          borderRadius: '50%',
                          backgroundColor: '#3b82f6',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '2px solid white',
                          fontSize: '0.55rem',
                          color: 'white',
                          fontWeight: 'bold'
                        }}>
                          ✓
                        </div>
                      )}
                    </div>
                    <span style={{
                      fontSize: '0.78rem',
                      fontWeight: isSelected ? '600' : '500',
                      color: isSelected ? '#1f2937' : '#6b7280'
                    }}>
                      {label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Plataforma Filters — mismo diseño que Tipo de Contenido */}
        {availablePlatforms.length > 0 && (
          <div style={{
            padding: '0.4rem',
            backgroundColor: '#f9fafb',
            borderRadius: '0.5rem',
            border: '1px solid #e5e7eb',
            marginBottom: '1rem'
          }}>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
              {availablePlatforms.map(({ name, icon }) => {
                const isSelected = selectedPlatforms.includes(name);
                const label = name.charAt(0).toUpperCase() + name.slice(1);
                return (
                  <button
                    key={name}
                    onClick={() => handlePlatformFilterClick(name)}
                    title={label}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.4rem',
                      padding: '0.3rem 0.55rem',
                      border: isSelected ? '3px solid #3b82f6' : '2px solid #e5e7eb',
                      borderRadius: '2rem',
                      backgroundColor: 'white',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      transform: isSelected ? 'scale(1.05)' : 'scale(1)',
                      boxShadow: isSelected ? '0 4px 6px -1px rgba(0, 0, 0, 0.1)' : 'none'
                    }}
                  >
                    <div style={{ position: 'relative' }}>
                      {icon ? (
                        <img
                          src={icon}
                          alt={label}
                          style={{ width: '22px', height: '22px', borderRadius: '50%', objectFit: 'cover', display: 'block' }}
                        />
                      ) : (
                        <div style={{
                          display: 'flex', width: '22px', height: '22px', borderRadius: '50%',
                          backgroundColor: '#e5e7eb', alignItems: 'center', justifyContent: 'center',
                          fontSize: '0.7rem', fontWeight: '600', color: '#6b7280'
                        }}>
                          {label.charAt(0)}
                        </div>
                      )}
                      {isSelected && (
                        <div style={{
                          position: 'absolute', bottom: '-2px', right: '-2px',
                          width: '13px', height: '13px', borderRadius: '50%',
                          backgroundColor: '#3b82f6', display: 'flex',
                          alignItems: 'center', justifyContent: 'center',
                          border: '2px solid white', fontSize: '0.55rem',
                          color: 'white', fontWeight: 'bold'
                        }}>
                          ✓
                        </div>
                      )}
                    </div>
                    <span style={{
                      fontSize: '0.78rem',
                      fontWeight: isSelected ? '600' : '500',
                      color: isSelected ? '#1f2937' : '#6b7280'
                    }}>
                      {label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Influencer Filters */}
        {availableInfluencers.length > 0 && (
          <div style={{
            padding: '0.4rem',
            backgroundColor: '#f9fafb',
            borderRadius: '0.5rem',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{
              display: 'flex',
              gap: '0.5rem',
              flexWrap: 'wrap',
              alignItems: 'center'
            }}>
              {availableInfluencers.map(influencer => {
                const isSelected = selectedInfluencers.includes(influencer.id);

                return (
                  <button
                    key={influencer.id}
                    onClick={() => handleInfluencerFilterClick(influencer.id)}
                    title={influencer.name}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.4rem',
                      padding: '0.3rem 0.55rem',
                      border: isSelected ? '3px solid #3b82f6' : '2px solid #e5e7eb',
                      borderRadius: '2rem',
                      backgroundColor: 'white',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      transform: isSelected ? 'scale(1.05)' : 'scale(1)',
                      boxShadow: isSelected ? '0 4px 6px -1px rgba(0, 0, 0, 0.1)' : 'none'
                    }}
                    onMouseOver={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.transform = 'scale(1.05)';
                        e.currentTarget.style.boxShadow = '0 2px 4px -1px rgba(0, 0, 0, 0.1)';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.boxShadow = 'none';
                      }
                    }}
                  >
                    <div style={{ position: 'relative' }}>
                      {(influencer.profile_image_url_saved || influencer.profile_image_url) ? (
                        <img
                          src={influencer.profile_image_url_saved || influencer.profile_image_url}
                          alt={influencer.name}
                          style={{
                            width: '22px',
                            height: '22px',
                            borderRadius: '50%',
                            objectFit: 'cover',
                            border: '2px solid white'
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextElementSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div
                        style={{
                          display: (influencer.profile_image_url_saved || influencer.profile_image_url) ? 'none' : 'flex',
                          width: '22px',
                          height: '22px',
                          borderRadius: '50%',
                          backgroundColor: '#e5e7eb',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.7rem',
                          fontWeight: '600',
                          color: '#6b7280'
                        }}
                      >
                        {getInitials(influencer.name)}
                      </div>
                      {isSelected && (
                        <div style={{
                          position: 'absolute',
                          bottom: '-2px',
                          right: '-2px',
                          width: '13px',
                          height: '13px',
                          borderRadius: '50%',
                          backgroundColor: '#3b82f6',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '2px solid white',
                          fontSize: '0.55rem',
                          color: 'white',
                          fontWeight: 'bold'
                        }}>
                          ✓
                        </div>
                      )}
                    </div>
                    <span style={{
                      fontSize: '0.78rem',
                      fontWeight: isSelected ? '600' : '500',
                      color: isSelected ? '#1f2937' : '#6b7280'
                    }}>
                      {influencer.name}
                    </span>
                  </button>
                );
              })}
            </div>

            <div style={{
              marginTop: '0.75rem',
              fontSize: '0.75rem',
              color: '#6b7280',
              display: 'flex',
              gap: '1rem'
            }}>
              <span>Total: {allEvents.length} posts</span>
              {(selectedStatuses.length > 0 || selectedInfluencers.length > 0 || selectedMaterialTypes.length > 0 || selectedPlatforms.length > 0 || searchTerm.trim() !== '') && (
                <span style={{ color: '#3b82f6', fontWeight: '600' }}>
                  Mostrando: {events.length} posts
                </span>
              )}
            </div>
          </div>
        )}

        </div>
      )}

      {/* Calendar */}
      {loadError && (
        <div className="calendar-load-error">
          <span>No se pudieron cargar los posts.</span>
          <button onClick={() => loadPosts()}>Reintentar</button>
        </div>
      )}
      {loading ? (
        <Loading fullScreen={false} />
      ) : (
        <div className="calendar-grid-wrapper" ref={gridWrapperRef}>
        <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
            initialView={currentView}
            initialDate={new Date()}
            locale={esLocale}
            headerToolbar={false}
            buttonText={{
              today: 'Hoy',
              month: 'Mes',
              week: 'Semana',
              day: 'Día',
              list: 'Lista'
            }}
            events={events}
            eventContent={renderEventContent}
            eventDidMount={handleEventDidMount}
            dateClick={handleDateClick}
            eventClick={handleEventClick}
            datesSet={handleDatesSet}
            height="auto"
            dayMaxEvents={false}
            moreLinkText="más"
            displayEventTime={true}
            firstDay={1}
            eventDisplay="block"
            eventClassNames="fc-custom-event"
            allDaySlot={false}
            slotMinTime="06:00:00"
            slotMaxTime="23:00:00"
            slotDuration="00:30:00"
            slotLabelInterval="01:00"
            eventTimeFormat={{
              hour: '2-digit',
              minute: '2-digit',
              meridiem: false,
              hour12: false
            }}
          />
        </div>
      )}

      {/* Modals */}
      {showCreateModal && (
        <PostModal
          post={selectedPost}
          onClose={handleModalClose}
          onUpdate={handleUpdate}
        />
      )}

      {showDetailModal && selectedPost && (
        <PostDetailModal
          postId={selectedPost.id}
          onClose={handleModalClose}
          onUpdate={handleUpdate}
        />
      )}

      {showBulkImport && (
        <BulkImportModal
          onClose={() => setShowBulkImport(false)}
          onSuccess={() => { loadPosts(); setShowBulkImport(false); }}
        />
      )}

      {/* Aviso de posts vencidos (1 vez al día por usuario). Solo para quienes
          tienen la pantalla "Vencidos" habilitada, igual que el menú lateral. */}
      <VencidosModal
        user={currentUser}
        enabled={!isCliente && tienePantalla(currentUser, 'vencidos')}
        onChanged={loadPosts}
        onDone={() => setVencidosDone(true)}
      />

      {/* Aviso de aprobaciones pendientes (1 vez al día por usuario), después del de vencidos.
          Solo para quienes tienen la pantalla "Aprobaciones" habilitada. */}
      <AprobacionesModal
        user={currentUser}
        enabled={tienePantalla(currentUser, 'aprobaciones')}
        ready={vencidosDone}
        onChanged={loadPosts}
        onDone={() => setAprobacionesDone(true)}
      />

      {/* Aviso de posts con validación próxima a los que les falta enviar idea creativa / copy / contenido
          (1 vez al día). El backend limita el alcance: Managers ven todos, el resto solo sus posts como editor. */}
      <PendientesEnvioModal
        user={currentUser}
        enabled={!isCliente}
        ready={aprobacionesDone}
        onChanged={loadPosts}
      />
    </div>
  );
};

export default CalendarView;
